import { Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@fleet/ui";
import { useI18n } from "@/providers/i18n-provider";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useCategoriesQuery, useTransactionsQuery, useWalletsQuery } from "@cashsync/shared/solid";
import { cn } from "@cashsync/shared";
import { BarChart3 } from "@fleet/icons";
import { COMPACT_THRESHOLD, formatCompactNumber } from "@fleet/utils";

interface StatsSectionProps {
  userId: string;
}

export function StatsSection(props: StatsSectionProps) {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { t } = useI18n();

  const deps = {
    db: () => db(),
    drizzle,
    userId: () => props.userId,
  };

  const wallets = useWalletsQuery(deps);
  const categories = useCategoriesQuery(deps);
  // Without range: counts all live transactions of the user.
  const transactions = useTransactionsQuery({ ...deps, filters: () => ({}) });

  return (
    <Card>
      <CardHeader>
        <div class="flex items-center gap-2">
          <BarChart3 class="h-5 w-5 text-primary" />
          <CardTitle>{t(msg`Statistics`)}</CardTitle>
        </div>
      </CardHeader>
      <CardContent class="space-y-6">
        <div class="grid grid-cols-3 gap-4">
          <StatCard
            label={t(msg`Transactions`)}
            value={transactions.data?.length ?? 0}
            colorClass="text-primary"
            bgClass="bg-primary/10"
            isLoading={transactions.isPending}
          />
          <StatCard
            label={t(msg`Accounts`)}
            value={wallets.data?.length ?? 0}
            colorClass="text-success"
            bgClass="bg-success/10"
            isLoading={wallets.isPending}
          />
          <StatCard
            label={t(msg`Categories`)}
            value={categories.data?.length ?? 0}
            colorClass="text-warning"
            bgClass="bg-warning/10"
            isLoading={categories.isPending}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function StatCard(props: {
  label: string;
  value: number;
  colorClass: string;
  bgClass: string;
  isLoading?: boolean;
}) {
  const { locale } = useI18n();
  const isCompact = () => props.value >= COMPACT_THRESHOLD;
  const formattedValue = () => formatCompactNumber(props.value, locale());
  const fullLabel = () => `${props.value.toLocaleString(locale())} ${props.label.toLowerCase()}`;

  return (
    <div
      class={cn(
        "flex flex-col items-center justify-center rounded-2xl p-4 text-center transition-all hover:scale-[1.02]",
        props.bgClass,
      )}
    >
      <Show
        when={props.isLoading}
        fallback={
          <Show
            when={isCompact()}
            fallback={
              <span
                class={cn(
                  "text-2xl font-black leading-none tracking-tight select-none",
                  props.colorClass,
                )}
                aria-label={fullLabel()}
              >
                {props.value}
              </span>
            }
          >
            <Tooltip openDelay={350} closeDelay={100}>
              <TooltipTrigger class="focus-visible:outline-none select-none cursor-help">
                <span
                  class={cn(
                    "text-2xl font-black leading-none tracking-tight border-b border-dashed border-current pb-0.5",
                    props.colorClass,
                  )}
                  aria-label={fullLabel()}
                >
                  {formattedValue()}
                </span>
              </TooltipTrigger>
              <TooltipContent class="max-w-[200px] text-center font-semibold bg-background text-foreground border border-border/40 shadow-lg px-2.5 py-1.5 rounded-lg select-none">
                {fullLabel()}
              </TooltipContent>
            </Tooltip>
          </Show>
        }
      >
        <Skeleton class="h-7 w-8" />
      </Show>
      <div class="mt-2 text-[10px] font-bold text-muted-foreground uppercase tracking-widest opacity-80 select-none">
        {props.label}
      </div>
    </div>
  );
}
